import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useMemo, useState, useEffect, useCallback } from 'react';

import {
  Box,
  Card,
  Table,
  alpha,
  Stack,
  Avatar,
  Button,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  IconButton,
  TableContainer,
} from '@mui/material';

import { useAuth } from 'src/hooks/useAuth';
import { useToast } from 'src/hooks/useToast';
import { useConfirmModal } from 'src/hooks/useModal';

import { genericApiToaster } from 'src/services/utils';
import { getUserAvatarUrl, getUsersByDepartment } from 'src/services/user.service';
import {
  deleteDepartment,
  unassignMemberFromDepartment,
} from 'src/services/department.service';

import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import TableToolbar from 'src/components/ToolBars/TableToolbar';

import FilterPopover from 'src/sections/users/popovers/filterPopover';

import AddMembersModal from './modal/AddMembersModal';
import ManageSubmittersModal from './modal/ManageSubmittersModal';

const TABLE_HEAD = [
  { id: 'name', label: 'attributes.name', alignRight: false },
  { id: 'email', label: 'pages.usersPage.form.emailAdress', alignRight: false },
  { id: 'role', label: 'pages.profilePage.role', alignRight: false },
  { id: 'options', label: '', alignRight: true },
];

export default function DepartmentMembersView({ department, onBack }) {
  const { t } = useTranslation();
  const { showToast, hideToast } = useToast();

  const { permissions } = useAuth();

  const canUpdate = permissions?.includes('DEPARTMENT:UPDATE');
  const canDelete = permissions?.includes('DEPARTMENT:DELETE');

  const [members, setMembers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openSubmittersModal, setOpenSubmittersModal] = useState(false);
  // eslint-disable-next-line
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // States to control the search/filter states and page paginations
  const [searchBy, setSearchBy] = useState('fullName');
  const [searchValue, setSearchValue] = useState('');
  const [filterBy, setFilterBy] = useState('');
  const [filterValue, setFilterValue] = useState('');

  // States to control the filter popover opening and nested selections
  const [filterPopover, setFilterPopover] = useState(null);
  const [subFilterPopover, setSubFilterPopover] = useState({
    department: null,
    status: null,
    role: null,
  });

  const handleOpenFilter = (event) => {
    setFilterPopover(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setFilterPopover(null);
  };

  const handleCloseSubFilter = () => {
    setSubFilterPopover({
      department: null,
      status: null,
      role: null,
    });
  };

  const dynamicTableHead = useMemo(() => {
    if (canUpdate) return TABLE_HEAD;
    return TABLE_HEAD.filter((cell) => cell.id !== 'options');
  }, [canUpdate]);

  // Dual-filtering selector: Filters the department members list dynamically by both text and category filters
  const filteredMembers = useMemo(() => {
    const resolved = (members || []).map((user) => ({
      ...user,
      fullName: `${user.givenName || ''} ${user.familyName || ''}`.trim(),
    }));

    let result = resolved;

    // First: Apply categorical dropdown filters (Role and Status)
    if (filterBy && filterValue) {
      const target = filterValue.toLowerCase().trim();
      result = result.filter((u) => {
        if (filterBy === 'status') return String(u.status) === target; // Enables dynamic status filtering (Active/Inactive)
        if (filterBy === 'role') return u.role === filterValue;
        return true;
      });
    }

    // Second: Apply text query search (ID, Name, Email)
    if (searchValue) {
      const query = searchValue.toLowerCase().trim();
      result = result.filter((u) => {
        if (searchBy === 'id') return String(u.id).includes(query);
        if (searchBy === 'fullName') return u.fullName.toLowerCase().includes(query);
        if (searchBy === 'email') return u.email && u.email.toLowerCase().includes(query);
        return true;
      });
    }

    return result;
  }, [members, searchValue, searchBy, filterBy, filterValue]);

  const loadMembers = useCallback(() => {
    setIsLoading(true);
    getUsersByDepartment(department.id).then((res) => {
      setMembers(res);
      setIsLoading(false);
    });
  }, [department.id]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const { showConfirmModal: confirmDeleteDept } = useConfirmModal(
    t('pages.departmentPage.details.confirmDelete'),
    `${t('pages.departmentPage.details.areYouSure')} ${department.name}?`,
    async () => {
      genericApiToaster(
        deleteDepartment,
        department.id,
        t('successMessages.successDepartmentDelete'),
        '',
        showToast,
        hideToast,
        t,
        onBack
      );
    }
  );

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelected(members.map((n) => n.id));
      return;
    }
    setSelected([]);
  };

  const handleClick = (id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleRemoveSingle = (userId) => {
    genericApiToaster(
      unassignMemberFromDepartment,
      [department.id, userId],
      t('successMessages.memberRemoved'),
      '',
      showToast,
      hideToast,
      t,
      loadMembers
    );
  };

  const processRemovalQueue = (queue) => {
    if (queue.length === 0) {
      setIsProcessing(false);
      showToast({
        message: t('successMessages.membersRemoved'),
        severity: 'success',
        props: { hideToast },
      });
      setSelected([]);
      loadMembers();
      return;
    }
    const [currentUserId, ...remaining] = queue;
    unassignMemberFromDepartment(department.id, currentUserId)
      .then(() => {
        setTimeout(() => processRemovalQueue(remaining), 250);
      })
      .catch(() => {
        setIsProcessing(false);
        loadMembers();
      });
  };

  const handleRemoveBulk = () => {
    if (selected.length === 0 || isProcessing) return;
    setIsProcessing(true);
    processRemovalQueue([...selected]);
  };

  return (
    <Box sx={{ p: 4, width: '100%', mx: 'auto' }}>
      {/* 1. HEADER */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={4}>
        <Box>
          <Button
            startIcon={<Iconify icon="solar:alt-arrow-left-bold-duotone" />}
            onClick={onBack}
            sx={{ fontWeight: 800, color: 'text.secondary' }}
          >
            {t('buttons.back')}
          </Button>
          <Typography variant="h3" sx={{ fontWeight: 800, color: 'text.primary', mt: 1 }}>
            {department.name}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5}>
          {canUpdate && selected.length > 0 && (
            <Button
              variant="contained"
              color="error"
              disabled={isProcessing}
              startIcon={
                <Box
                  component="img"
                  src="/assets/icons/Tickets/deleteuser.svg"
                  sx={{ width: 20, height: 20, filter: 'brightness(0) invert(1)' }}
                />
              }
              onClick={handleRemoveBulk}
              sx={{ borderRadius: '12px', fontWeight: 800, px: 3 }}
            >
              {isProcessing
                ? t('buttons.removing')
                : `${t('buttons.removeSelected')} (${selected.length})`}
            </Button>
          )}

          {/* NEW: Ticket Submitters Button (Placed beside Add Members) */}
          {canUpdate && (
            <Button
              variant="contained"
              startIcon={<Iconify icon="solar:home-wifi-bold-duotone" />}
              onClick={() => setOpenSubmittersModal(true)}
              sx={{
                bgcolor: '#00A76F',
                borderRadius: '12px',
                fontWeight: 800,
                px: 3,
                '&:hover': { bgcolor: '#008F5D' },
              }}
            >
              {t('tickettt.ticketSubmitters')}
            </Button>
          )}

          {canUpdate && (
            <Button
              variant="contained"
              startIcon={
                <Box
                  component="img"
                  src="/assets/icons/Tickets/adduser.svg"
                  sx={{ width: 20, height: 20, filter: 'brightness(0) invert(1)' }}
                />
              }
              onClick={() => setOpenAddModal(true)}
              sx={{
                bgcolor: '#00A76F',
                borderRadius: '12px',
                fontWeight: 800,
                px: 4,
                '&:hover': { bgcolor: '#008F5D' },
              }}
            >
              {t('ticket.addMembers')}
            </Button>
          )}
        </Stack>
      </Stack>

      {/* 2. TABLE */}
      <Card
        sx={{
          borderRadius: '24px',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 4px 20px rgba(0,0,0,0.02)',
          mb: 4,
        }}
      >
        {/* Table Toolbar incorporating dynamic search selections */}
        <TableToolbar
          numSelected={canUpdate ? selected.length : 0}
          filterName={searchValue}
          setFilterName={setSearchValue}
          filterBy={searchBy}
          setFilterBy={setSearchBy}
          handleOpenFilter={handleOpenFilter}
          handleCloseFilter={handleCloseFilter}
          filterValue={filterValue}
          setFilterValue={setFilterValue}
          onFilterName={(e) => {
            setSearchValue(e.target.value);
          }}
          selected={selected}
          handleRemoveSelected={() => setSelected([])}
          deleteFunc={handleRemoveBulk}
          model={t('nav.users')}
          data={members}
        />
        <Scrollbar>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {canUpdate && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={
                          selected.length > 0 && selected.length < members.length
                        }
                        checked={members.length > 0 && selected.length === members.length}
                        onChange={handleSelectAllClick}
                        sx={{ color: '#00A76F', '&.Mui-checked': { color: '#00A76F' } }}
                      />
                    </TableCell>
                  )}
                  {dynamicTableHead.map((headCell) => (
                    <TableCell
                      key={headCell.id}
                      align={headCell.alignRight ? 'right' : 'left'}
                      sx={{
                        fontWeight: 800,
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        fontSize: '0.7rem',
                      }}
                    >
                      {headCell.label ? t(headCell.label) : ''}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMembers.map((row) => {
                  // Fixed: Maps over the dual-filtered list
                  const isItemSelected = selected.indexOf(row.id) !== -1;
                  return (
                    <TableRow key={row.id} hover selected={isItemSelected}>
                      {canUpdate && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isItemSelected}
                            onChange={() => handleClick(row.id)}
                            sx={{
                              color: '#00A76F',
                              '&.Mui-checked': { color: '#00A76F' },
                            }}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar
                            src={
                              row.avatarVersion
                                ? getUserAvatarUrl(row.id, row.avatarVersion)
                                : null
                            }
                            sx={{
                              width: 40,
                              height: 40,
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                            imgProps={{ loading: 'lazy' }}
                          />
                          <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                            {row.givenName} {row.familyName}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        {row.email}
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            textTransform: 'uppercase',
                            fontSize: '0.65rem',
                            letterSpacing: '0.05em',
                            fontWeight: 900,
                            color: '#2065D1',
                            bgcolor: alpha('#2065D1', 0.08),
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '6px',
                            display: 'inline-flex',
                          }}
                        >
                          {row.role?.replace('ROLE_', '').replace(/_/g, ' ')}
                        </Box>
                      </TableCell>

                      {canUpdate && (
                        <TableCell align="right">
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveSingle(row.id)}
                            disabled={isProcessing}
                          >
                            <Iconify icon="solar:trash-bin-minimalistic-bold-duotone" />
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>
      </Card>

      {/* 3. FOOTER ACTIONS */}
      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        {canDelete && (
          <Button
            variant="outlined"
            color="error"
            disabled={members.length > 0 || isProcessing}
            startIcon={<Iconify icon="solar:trash-bin-trash-bold-duotone" />}
            onClick={confirmDeleteDept}
            sx={{ fontWeight: 800, borderRadius: '10px', px: 3 }}
          >
            {t('buttons.deleteDepartment')}
          </Button>
        )}
      </Stack>

      <AddMembersModal
        open={openAddModal}
        onClose={() => {
          setOpenAddModal(false);
          loadMembers();
        }}
        department={department}
      />

      {/* NEW: Manage Submitters Modal */}
      <ManageSubmittersModal
        open={openSubmittersModal}
        onClose={() => setOpenSubmittersModal(false)}
        department={department}
      />

      {/* Dynamic Filter Popover for Department Members */}
      <FilterPopover
        data={members}
        setSubFilterPopover={setSubFilterPopover}
        subFilterPopover={subFilterPopover}
        handleCloseSubFilter={handleCloseSubFilter}
        setFilterBy={setFilterBy} // Category filter state
        setFilterName={setFilterValue} // Category filter value state (maps selected option directly to filterValue)
        setSearchBy={setSearchBy}
        departmentsData={[]} // No department sub-menu needed for member role/status filtering
        handleCloseFilter={handleCloseFilter}
        filterPopover={filterPopover}
      />
    </Box>
  );
}

DepartmentMembersView.propTypes = {
  department: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
};
